import Pagination from "@components/Pagination";
import DealCouponModal from "@components/_shared/DealCouponModal";
import Image from "@components/_shared/NextImage";
import { useSiteConfig } from "@context/siteConfig";
import { useGenzoTracking } from "@context/tracking/genzo";
import { useTranslations } from "@context/translations";
import {
  getCouponClickEvent,
  getCouponPageDealCouponModalClickEvent,
} from "@helpers/genzo/genzoEventActionsBuilder";
import { DealCouponModalCtaType } from "@shopcashTypes/dealCouponModalCtaType";
import { GenzoCouponCardCtaType } from "@shopcashTypes/genzo/genzoCouponCardCtaType";
import { GenzoDealCouponModalAction } from "@shopcashTypes/genzoDealCouponModalAction";
import clsx from "clsx";
import React, { useState } from "react";
import CouponCard from "./CouponCard";
import styles from "./CouponList.module.scss";

interface CouponListProps {
  deals: Array<StoreDeal> | undefined;
  pagination: Pagination;
  onPaginationChange: (newPage: number) => void;
}

const CouponList: React.FC<CouponListProps> = ({
  deals,
  pagination,
  onPaginationChange,
}) => {
  const { isRtl } = useSiteConfig();
  const [showModal, setShowModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<StoreDeal>();
  const translate = useTranslations();
  const { trackEvent } = useGenzoTracking();

  const handleOnShowCodeClick = (
    deal: StoreDeal,
    isLoggedIn: boolean
  ): void => {
    setSelectedDeal(deal);
    setShowModal(true);

    if (isLoggedIn) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigator?.clipboard?.writeText(deal.couponCode);
    }
  };

  const handleDealCouponModalClose = (): void => {
    setSelectedDeal(null);
    setShowModal(false);
  };

  const handleCouponCardEventTrack = (
    ctaType: GenzoCouponCardCtaType,
    deal: StoreDeal,
    index: number
  ): void => {
    switch (ctaType) {
      case GenzoCouponCardCtaType.ShowCodeButton:
        trackEvent(getCouponClickEvent(deal, index));
        break;
    }
  };

  const handleDealCouponModalEventTrack = (
    ctaType: DealCouponModalCtaType,
    deal: StoreDeal
  ): void => {
    switch (ctaType) {
      case DealCouponModalCtaType.CopyCode:
        trackEvent(
          getCouponPageDealCouponModalClickEvent(
            deal,
            GenzoDealCouponModalAction.CopyCode
          )
        );
        break;
      case DealCouponModalCtaType.SignInForCode:
        trackEvent(
          getCouponPageDealCouponModalClickEvent(
            deal,
            GenzoDealCouponModalAction.SignInForCode
          )
        );
        break;
      case DealCouponModalCtaType.ShopNow:
        trackEvent(
          getCouponPageDealCouponModalClickEvent(
            deal,
            GenzoDealCouponModalAction.ShopNow
          )
        );
        break;
      case DealCouponModalCtaType.StoreLogo:
        trackEvent(
          getCouponPageDealCouponModalClickEvent(
            deal,
            GenzoDealCouponModalAction.StoreLogo
          )
        );
        break;
      case DealCouponModalCtaType.ViewMoreDeals:
        trackEvent(
          getCouponPageDealCouponModalClickEvent(
            deal,
            GenzoDealCouponModalAction.ViewMoreDeals
          )
        );
        break;
    }
  };

  return (
    <>
      <div
        data-testid="coupon-list"
        className={clsx(styles.container, isRtl && styles.rtl)}
      >
        {deals && deals.length > 0 && (
          <div className={styles.dealsContainer}>
            {deals.map((deal, i) => (
              <div key={deal.id} className={styles.dealCard}>
                <CouponCard
                  deal={deal}
                  onEventTrack={(ctaType: GenzoCouponCardCtaType) =>
                    handleCouponCardEventTrack(ctaType, deal, i)
                  }
                  onShowCodeClick={handleOnShowCodeClick}
                />
              </div>
            ))}
          </div>
        )}

        {deals && deals.length === 0 && (
          <div
            className={styles.emptyContainer}
            data-testid="coupon-list-empty-state"
          >
            <Image
              src="https://assets.wego.com/image/upload/v1604479049/web/shopcash/illustration-price-tag.png"
              alt="No Coupons found"
              width="200"
              height="200"
            />

            <div className={styles.emptyMessage}>
              {translate("empty_coupons")}
            </div>
          </div>
        )}

        {showModal && !!selectedDeal && (
          <DealCouponModal
            show={showModal}
            deal={selectedDeal}
            onCloseClick={handleDealCouponModalClose}
            onEventTrack={(ctaType: DealCouponModalCtaType) =>
              handleDealCouponModalEventTrack(ctaType, selectedDeal)
            }
          ></DealCouponModal>
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div
          className={styles.paginationContainer}
          data-testid="coupon-list-pagination"
        >
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={onPaginationChange}
          />
        </div>
      )}
    </>
  );
};

export default CouponList;
